import Auth from "@Front/Api/Auth/Customer/Auth";
import { reportIdNotRemoteLogoutSkipped } from "@Front/Api/Auth/IdNot/helpers/idNotRemoteLogoutSkipped";
import { buildIdNotOAuthRedirectUri, readIdNotReturnToFromSessionStorage } from "@Front/Api/Auth/IdNot/helpers/idNotOAuthRedirectUriBrowser";
import { resolveIdNotOAuthPublicBaseUrl } from "@Front/Api/Auth/IdNot/helpers/idNotOAuthPublicBaseUrl";
import { FrontendVariables } from "@Front/Config/VariablesFront";
import { loadPublicConfigRuntime } from "@Front/Config/ConfigRuntime";
import { CustomerStore } from "@Front/Stores/CustomerStore";
import { UserStore } from "@Front/Stores/UserStore";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { resolveActiveAuthContextFromSession } from "@Front/Api/helpers/authTokenResolutionHelpers";

/**
 * Clean up all local storage and cookies
 */
async function cleanupLocalSession(isCustomer: boolean): Promise<void> {
	if (isCustomer) {
		await CustomerStore.instance.disconnect();
	} else {
		await UserStore.instance.disconnect();
	}

	document.cookie.split(";").forEach((cookie) => {
		document.cookie = cookie.replace(/^ +/, "").replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
	});
	sessionStorage.clear();
	localStorage.clear();
}

interface BuildIdNotAuthorizationUrlOptions {
	baseUrl: string;
	authorizeEndpoint: string;
	clientId: string;
	appHost: string;
	returnTo: string | null;
}

function buildIdNotAuthorizationUrl(opts: BuildIdNotAuthorizationUrlOptions): URL {
	const { baseUrl, authorizeEndpoint, clientId, appHost, returnTo } = opts;
	const authorizationUrl = new URL(authorizeEndpoint, baseUrl);
	const redirectUri = buildIdNotOAuthRedirectUri(appHost, returnTo);

	authorizationUrl.searchParams.set("client_id", clientId);
	authorizationUrl.searchParams.set("redirect_uri", redirectUri);
	authorizationUrl.searchParams.set("scope", "openid,profile");
	authorizationUrl.searchParams.set("response_type", "code");
	authorizationUrl.searchParams.set("prompt", "login");

	return authorizationUrl;
}

async function revokeLeCoffreTokenIfConnected(): Promise<void> {
	const hasToken =
		resolveActiveAuthContextFromSession() !== null ||
		UserStore.instance.isConnected() ||
		CustomerStore.instance.isConnected();
	if (!hasToken) {
		return;
	}
	try {
		await Auth.getInstance().logout();
	} catch {
		/* ignore — token may already be revoked */
	}
}

async function cleanupNotaryIdNotCookiesIfNeeded(isNotary: boolean): Promise<void> {
	if (!isNotary) {
		return;
	}
	await reportIdNotRemoteLogoutSkipped("Login pré-IdNot");
}

/**
 * Clean up local session before IdNot connection
 */
async function cleanupSessionBeforeIdNot(resolveRoleFlags: () => { isCustomer: boolean; isNotary: boolean }): Promise<void> {
	try {
		const { isCustomer, isNotary } = resolveRoleFlags();
		await revokeLeCoffreTokenIfConnected();
		await cleanupNotaryIdNotCookiesIfNeeded(isNotary);
		await cleanupLocalSession(isCustomer);
	} catch {
		/* continue toward IdNot redirect */
	}
}

function isFrontendIdNotConfigIncomplete(variables: FrontendVariables): boolean {
	const canResolvePublicOrigin =
		(typeof window !== "undefined" && Boolean(window.location?.origin)) || Boolean(variables.FRONT_APP_HOST?.trim());
	return (
		!variables.IDNOT_CLIENT_ID ||
		!variables.IDNOT_BASE_URL ||
		!variables.IDNOT_AUTHORIZE_ENDPOINT ||
		!canResolvePublicOrigin
	);
}

function resolveFrontAppHostForIdNot(): string | undefined {
	const resolved = resolveIdNotOAuthPublicBaseUrl();
	return resolved.length > 0 ? resolved : undefined;
}

function buildResolvedIdNotConfig(variables: FrontendVariables): {
	baseUrl: string;
	authorizeEndpoint: string;
	clientId: string;
	appHost: string;
} | null {
	const baseUrl = variables.IDNOT_BASE_URL;
	const authorizeEndpoint = variables.IDNOT_AUTHORIZE_ENDPOINT;
	const clientId = variables.IDNOT_CLIENT_ID;
	const appHost = resolveFrontAppHostForIdNot();
	if (!baseUrl || !authorizeEndpoint || !clientId || !appHost) {
		return null;
	}
	return { baseUrl, authorizeEndpoint, clientId, appHost };
}

/**
 * Load IdNot configuration if needed
 */
export async function ensureIdNotConfig(): Promise<{
	baseUrl: string;
	authorizeEndpoint: string;
	clientId: string;
	appHost: string;
} | null> {
	const variables = FrontendVariables.getInstance();
	if (isFrontendIdNotConfigIncomplete(variables)) {
		await loadPublicConfigRuntime();
	}
	return buildResolvedIdNotConfig(FrontendVariables.getInstance());
}

/**
 * Redirect user to IdNot for connection
 */
export async function redirectToIdNot(
	resolveRoleFlags: () => { isCustomer: boolean; isNotary: boolean },
	openErrorModal: (index: number) => void,
): Promise<void> {
	await cleanupSessionBeforeIdNot(resolveRoleFlags);

	const config = await ensureIdNotConfig();
	if (!config) {
		openErrorModal(4);
		return;
	}

	const { baseUrl, authorizeEndpoint, clientId, appHost } = config;
	const returnTo = readIdNotReturnToFromSessionStorage();

	try {
		const authorizationUrl = buildIdNotAuthorizationUrl({
			baseUrl,
			authorizeEndpoint,
			clientId,
			appHost,
			returnTo,
		});
		window.location.assign(authorizationUrl.toString());
	} catch (error: unknown) {
		LoggerService.error("Unable to build IdNot authorization URL", { error });
		openErrorModal(4);
	}
}
